import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Row, Col, Card, Button, Tag, Modal, Form, Input, Select, DatePicker,
  Spin, Typography, Space, Timeline, Badge, Tooltip, Popconfirm
} from 'antd';
import {
  PlusOutlined, CommentOutlined, ClockCircleOutlined, UserOutlined,
  DeleteOutlined, LockOutlined, CalendarOutlined,
  ClearOutlined, SearchOutlined, TeamOutlined, ShoppingCartOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';
import { internalTaskApi, InternalTask, TaskDetail, TaskUser } from '../services/internalTaskApi';
import api from '../services/api';
import {
  CustomerOptionDto,
  TaskOrderOptionDto,
  normalizeCustomerOptions,
  normalizeTaskOrderOptions,
} from '../dto/reference';
import { canDeleteInternalTask } from '../modules/tasks/internalTaskAccessPolicy';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import './InternalTasks.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

const STATUSES = ['NEW', 'IN_PROGRESS', 'REVIEW', 'DONE', 'CANCELLED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const STATUS_COLORS: Record<string, string> = {
  NEW: 'default',
  IN_PROGRESS: 'processing',
  REVIEW: 'warning',
  DONE: 'success',
  CANCELLED: 'error'
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'default',
  MEDIUM: 'blue',
  HIGH: 'orange',
  URGENT: 'red'
};

const COLUMN_COLORS: Record<string, string> = {
  NEW: '#d9d9d9',
  IN_PROGRESS: '#1890ff',
  REVIEW: '#faad14',
  DONE: '#52c41a',
  CANCELLED: '#ff4d4f'
};

const InternalTasks: React.FC = () => {
  const { t } = useTranslation(['tasks', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<InternalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Reference data
  const [users, setUsers] = useState<TaskUser[]>([]);
  const [customers, setCustomers] = useState<CustomerOptionDto[]>([]);
  const [orders, setOrders] = useState<TaskOrderOptionDto[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterPriority, setFilterPriority] = useState<string[]>([]);
  const [filterAssignee, setFilterAssignee] = useState<string | undefined>(undefined);
  const [filterCustomer, setFilterCustomer] = useState<string | undefined>(undefined);
  const [filterOrder, setFilterOrder] = useState<string | undefined>(undefined);
  const [filterDueDate, setFilterDueDate] = useState<string | undefined>(undefined);
  const [filterSearch, setFilterSearch] = useState('');

  const user = authService.getCurrentUser();

  // Fetch reference data once
  useEffect(() => {
    Promise.all([
      internalTaskApi.getUsers().then(setUsers).catch(() => {}),
      api.get('/customers').then(r => setCustomers(normalizeCustomerOptions(r.data, 'tasks.customers'))).catch(() => {}),
      api.get('/orders').then(r => setOrders(normalizeTaskOrderOptions(r.data, 'tasks.orders'))).catch(() => {})
    ]);
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const filters: Record<string, string> = {};
      if (filterStatus.length) filters.status = filterStatus.join(',');
      if (filterPriority.length) filters.priority = filterPriority.join(',');
      if (filterAssignee) filters.assigneeId = filterAssignee;
      if (filterCustomer) filters.customerId = filterCustomer;
      if (filterOrder) filters.orderId = filterOrder;
      if (filterDueDate) filters.dueDate = filterDueDate;
      if (filterSearch.trim()) filters.search = filterSearch.trim();

      const data = await internalTaskApi.getTasks(filters);
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch internal tasks:', error);
      feedback.error(t('tasks:messages.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterAssignee, filterCustomer, filterOrder, filterDueDate, filterSearch, t]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, InternalTask[]> = {};
    STATUSES.forEach(s => { grouped[s] = []; });
    tasks.forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });
    return grouped;
  }, [tasks]);

  const hasActiveFilters = filterStatus.length > 0 || filterPriority.length > 0 ||
    filterAssignee || filterCustomer || filterOrder || filterDueDate || filterSearch.trim();

  const clearFilters = () => {
    setFilterStatus([]);
    setFilterPriority([]);
    setFilterAssignee(undefined);
    setFilterCustomer(undefined);
    setFilterOrder(undefined);
    setFilterDueDate(undefined);
    setFilterSearch('');
  };

  const handleCreateTask = async (values: any) => {
    setSubmitting(true);
    try {
      await internalTaskApi.createTask({
        title: values.title,
        description: values.description,
        priority: values.priority || 'MEDIUM',
        visibility: values.visibility || 'PUBLIC',
        assigneeId: values.assigneeId || null,
        customerId: values.customerId || null,
        orderId: values.orderId || null,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null
      });
      feedback.success(t('tasks:messages.createSuccess', { title: values.title }));
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchTasks();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.createError', undefined, { title: values.title }));
    } finally {
      setSubmitting(false);
    }
  };

  const openTaskDetail = async (taskId: string) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const task = await internalTaskApi.getTask(taskId);
      setSelectedTask(task);
    } catch (error) {
      feedback.error(t('tasks:messages.detailFetchError'));
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open a task directly from a notification deep link (/app/tasks?task=<id>),
  // then drop the param so a manual close/refresh does not reopen it.
  useEffect(() => {
    const deepLinkTaskId = searchParams.get('task');
    if (deepLinkTaskId) {
      openTaskDetail(deepLinkTaskId);
      searchParams.delete('task');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    // Every call site passes selectedTask.id, so selectedTask (if set) always
    // names the task being changed here.
    const title = selectedTask?.title || '';
    try {
      await internalTaskApi.updateStatus(taskId, newStatus);
      feedback.success(t('tasks:messages.statusChanged', { title, status: t(`tasks:statuses.${newStatus}`) }));
      fetchTasks();
      if (selectedTask?.id === taskId) {
        openTaskDetail(taskId);
      }
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.statusChangeError', undefined, { title }));
    }
  };

  const handleAddComment = async (values: any) => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      await internalTaskApi.addComment(selectedTask.id, values.content);
      feedback.success(t('tasks:messages.commentAdded', { title: selectedTask.title }));
      commentForm.resetFields();
      openTaskDetail(selectedTask.id);
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.commentError', undefined, { title: selectedTask.title }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const title = selectedTask?.title || '';
    try {
      await internalTaskApi.deleteTask(taskId);
      feedback.success(t('tasks:messages.deleteSuccess', { title }));
      setDetailModalVisible(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.deleteError', undefined, { title }));
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString();

  // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift
  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const isDueDateOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    const due = parseLocalDate(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{t('tasks:title')}</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          {t('tasks:newTask')}
        </Button>
      </div>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder={t('tasks:filters.search')}
              prefix={<SearchOutlined />}
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={3}>
            <Select
              mode="multiple"
              placeholder={t('tasks:filters.status')}
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
              maxTagCount={1}
              allowClear
            >
              {STATUSES.map(s => (
                <Select.Option key={s} value={s}>
                  <Tag color={STATUS_COLORS[s]}>{t(`tasks:statuses.${s}`)}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6} lg={3}>
            <Select
              mode="multiple"
              placeholder={t('tasks:filters.priority')}
              value={filterPriority}
              onChange={setFilterPriority}
              style={{ width: '100%' }}
              maxTagCount={1}
              allowClear
            >
              {PRIORITIES.map(p => (
                <Select.Option key={p} value={p}>
                  <Tag color={PRIORITY_COLORS[p]}>{t(`tasks:priorities.${p}`)}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6} lg={3}>
            <Select
              placeholder={t('tasks:filters.assignee')}
              value={filterAssignee}
              onChange={setFilterAssignee}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {users.map(u => (
                <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6} lg={3}>
            <Select
              placeholder={t('tasks:filters.customer')}
              value={filterCustomer}
              onChange={setFilterCustomer}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {customers.map(c => (
                <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6} lg={3}>
            <Select
              placeholder={t('tasks:filters.order')}
              value={filterOrder}
              onChange={setFilterOrder}
              style={{ width: '100%' }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.searchText ?? '').includes(input.toLowerCase())
              }
            >
              {orders.map(o => (
                <Select.Option key={o.id} value={o.id} searchText={o.searchText}>
                  {o.label || o.orderNumber}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6} lg={3}>
            <Select
              placeholder={t('tasks:filters.dueDate')}
              value={filterDueDate}
              onChange={setFilterDueDate}
              style={{ width: '100%' }}
              allowClear
            >
              <Select.Option value="overdue">{t('tasks:filters.dueDateOptions.overdue')}</Select.Option>
              <Select.Option value="today">{t('tasks:filters.dueDateOptions.today')}</Select.Option>
              <Select.Option value="this_week">{t('tasks:filters.dueDateOptions.thisWeek')}</Select.Option>
              <Select.Option value="no_date">{t('tasks:filters.dueDateOptions.noDate')}</Select.Option>
            </Select>
          </Col>
          {hasActiveFilters && (
            <Col>
              <Button icon={<ClearOutlined />} onClick={clearFilters} size="small">
                {t('tasks:filters.clear')}
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Kanban Columns */}
      <Row gutter={12} style={{ overflowX: 'auto', flexWrap: 'nowrap', minHeight: 'calc(100vh - 300px)' }}>
        {STATUSES.map(status => (
          <Col key={status} style={{ minWidth: 280, flex: '1 1 0' }}>
            <Card
              size="small"
              className="internal-tasks-board-column"
              title={
                <Space>
                  <Badge color={COLUMN_COLORS[status]} />
                  <span>{t(`tasks:columns.${status}`)}</span>
                  <Tag>{tasksByStatus[status].length}</Tag>
                </Space>
              }
              styles={{ body: { padding: 8 } }}
            >
              {tasksByStatus[status].length === 0 ? (
                <div className="internal-tasks-board-empty">
                  <Text type="secondary">{t('tasks:noTasks')}</Text>
                </div>
              ) : (
                tasksByStatus[status].map(task => (
                  <Card
                    key={task.id}
                    size="small"
                    hoverable
                    onClick={() => openTaskDetail(task.id)}
                    style={{ marginBottom: 8, cursor: 'pointer' }}
                  >
                    {/* Priority + Private */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Tag color={PRIORITY_COLORS[task.priority]}>
                        {t(`tasks:priorities.${task.priority}`)}
                      </Tag>
                      {task.visibility === 'PRIVATE' && (
                        <Tooltip title={t('tasks:visibilityOptions.privateHint')}>
                          <LockOutlined className="internal-tasks-muted-icon" />
                        </Tooltip>
                      )}
                    </div>

                    {/* Title */}
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                      {task.title}
                    </Text>

                    {/* Customer / Order links */}
                    {(task.customerName || task.displayOrderNumber || task.orderNumber) && (
                      <div style={{ marginBottom: 6, fontSize: 12 }}>
                        {task.customerName && (
                          <Tag color="cyan" style={{ fontSize: 11 }}>
                            <TeamOutlined /> {task.customerName}
                          </Tag>
                        )}
                        {(task.displayOrderNumber || task.orderNumber) && (
                          <Tag color="geekblue" style={{ fontSize: 11 }}>
                            <ShoppingCartOutlined /> {task.displayOrderNumber || task.orderNumber}
                          </Tag>
                        )}
                      </div>
                    )}

                    {/* Footer: assignee, due date, comments */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {task.assigneeName ? (
                          <><UserOutlined /> {task.assigneeName}</>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                            {t('tasks:unassigned')}
                          </Text>
                        )}
                      </Text>
                      <Space size={8}>
                        {task.dueDate && (
                          <Tooltip title={t('tasks:dueDate')}>
                            <Tag
                              color={isDueDateOverdue(task.dueDate) && task.status !== 'DONE' && task.status !== 'CANCELLED' ? 'red' : undefined}
                              style={{ fontSize: 11, margin: 0 }}
                            >
                              <CalendarOutlined /> {parseLocalDate(task.dueDate).toLocaleDateString()}
                            </Tag>
                          </Tooltip>
                        )}
                        {task.commentCount > 0 && (
                          <Tooltip title={t('tasks:comments')}>
                              <Space size={2}>
                                <CommentOutlined className="internal-tasks-muted-icon" />
                              <Text type="secondary" style={{ fontSize: 12 }}>{task.commentCount}</Text>
                            </Space>
                          </Tooltip>
                        )}
                      </Space>
                    </div>
                  </Card>
                ))
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Create Task Modal */}
      <Modal
        title={t('tasks:newTask')}
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); createForm.resetFields(); }}
        maskClosable={false}
        footer={null}
        width={650}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateTask} onFinishFailed={() => feedback.warning(t('tasks:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="title"
            label={t('tasks:taskTitle')}
            rules={[{ required: true, message: t('tasks:taskTitle') + ' is required' }]}
          >
            <Input placeholder={t('tasks:taskTitlePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('tasks:taskDescription')}>
            <TextArea rows={3} placeholder={t('tasks:taskDescriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="priority" label={t('tasks:priority')} initialValue="MEDIUM">
                <Select>
                  {PRIORITIES.map(p => (
                    <Select.Option key={p} value={p}>
                      <Tag color={PRIORITY_COLORS[p]}>{t(`tasks:priorities.${p}`)}</Tag>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="assigneeId" label={t('tasks:assignee')}>
                <Select placeholder={t('tasks:assigneePlaceholder')} allowClear showSearch optionFilterProp="children">
                  {users.map(u => (
                    <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dueDate" label={t('tasks:dueDate')}>
                <DatePicker style={{ width: '100%' }} placeholder={t('tasks:dueDatePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="customerId" label={t('tasks:customer')}>
                <Select placeholder={t('tasks:customerPlaceholder')} allowClear showSearch optionFilterProp="children">
                  {customers.map(c => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="orderId" label={t('tasks:order')}>
                <Select
                  placeholder={t('tasks:orderPlaceholder')}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.searchText ?? '').includes(input.toLowerCase())
                  }
                >
                  {orders.map(o => (
                    <Select.Option key={o.id} value={o.id} searchText={o.searchText}>
                      {o.label || o.orderNumber}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="visibility" label={t('tasks:visibility')} initialValue="PUBLIC">
                <Select>
                  <Select.Option value="PUBLIC">{t('tasks:visibilityOptions.PUBLIC')}</Select.Option>
                  <Select.Option value="PRIVATE">
                    <Space><LockOutlined />{t('tasks:visibilityOptions.PRIVATE')}</Space>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCreateModalVisible(false); createForm.resetFields(); }}>
                {t('common:cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {t('tasks:newTask')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        title={selectedTask?.title || t('tasks:taskDetail')}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setSelectedTask(null); }}
        maskClosable={false}
        footer={null}
        width={700}
      >
        {detailLoading ? (
          <Spin style={{ display: 'block', margin: '40px auto' }} />
        ) : selectedTask ? (
          <div>
            {/* Status + Priority + Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Space>
                <Tag color={STATUS_COLORS[selectedTask.status]}>
                  {t(`tasks:statuses.${selectedTask.status}`)}
                </Tag>
                <Tag color={PRIORITY_COLORS[selectedTask.priority]}>
                  {t(`tasks:priorities.${selectedTask.priority}`)}
                </Tag>
                {selectedTask.visibility === 'PRIVATE' && (
                  <Tag icon={<LockOutlined />}>{t('tasks:visibilityOptions.PRIVATE')}</Tag>
                )}
              </Space>
              <Space>
                <Select
                  value={selectedTask.status}
                  onChange={(val) => handleStatusChange(selectedTask.id, val)}
                  style={{ width: 150 }}
                  size="small"
                >
                  {STATUSES.map(s => (
                    <Select.Option key={s} value={s}>
                      {t(`tasks:statuses.${s}`)}
                    </Select.Option>
                  ))}
                </Select>
                {canDeleteInternalTask(selectedTask, user) && (
                  <Popconfirm
                    title={t('tasks:deleteConfirm')}
                    onConfirm={() => { void handleDeleteTask(selectedTask.id); }}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            </div>

            {/* Description */}
            {selectedTask.description && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedTask.description}</Text>
              </Card>
            )}

            {/* Meta info */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">{t('tasks:assignee')}:</Text>{' '}
                  <Text strong>{selectedTask.assigneeName || t('tasks:unassigned')}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{t('tasks:createdBy')}:</Text>{' '}
                  <Text strong>{selectedTask.createdByName}</Text>
                </Col>
                {selectedTask.customerName && (
                  <Col span={12}>
                    <Text type="secondary">{t('tasks:customer')}:</Text>{' '}
                    <Text
                      strong
                      className="internal-tasks-link"
                      onClick={() => { /* future: navigate to customer detail */ }}
                    >
                      {selectedTask.customerName}
                    </Text>
                  </Col>
                )}
                {(selectedTask.displayOrderNumber || selectedTask.orderNumber) && (
                  <Col span={12}>
                    <Text type="secondary">{t('tasks:order')}:</Text>{' '}
                    <Text
                      strong
                      className="internal-tasks-link"
                      onClick={() => navigate(`/app/orders/${selectedTask.orderId}`)}
                    >
                      {selectedTask.displayOrderNumber || selectedTask.orderNumber}
                    </Text>
                  </Col>
                )}
                {selectedTask.dueDate && (
                  <Col span={12}>
                    <Text type="secondary">{t('tasks:dueDate')}:</Text>{' '}
                    <Tag
                      color={isDueDateOverdue(selectedTask.dueDate) && selectedTask.status !== 'DONE' && selectedTask.status !== 'CANCELLED' ? 'red' : undefined}
                    >
                      <CalendarOutlined /> {parseLocalDate(selectedTask.dueDate).toLocaleDateString()}
                    </Tag>
                    {isDueDateOverdue(selectedTask.dueDate) && selectedTask.status !== 'DONE' && selectedTask.status !== 'CANCELLED' && (
                      <Text type="danger" style={{ fontSize: 12, marginLeft: 4 }}>{t('tasks:overdue')}</Text>
                    )}
                  </Col>
                )}
                <Col span={12}>
                  <Text type="secondary">{t('tasks:createdAt')}:</Text>{' '}
                  <Text>{formatDate(selectedTask.createdAt)}</Text>
                </Col>
              </Row>
            </Card>

            {/* Comments */}
            <Title level={5} style={{ marginBottom: 12 }}>
              <CommentOutlined /> {t('tasks:comments')}
            </Title>

            {selectedTask.comments.length === 0 ? (
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                {t('tasks:noComments')}
              </Text>
            ) : (
              <div style={{ marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
                {selectedTask.comments.map(comment => (
                  <Card key={comment.id} size="small" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 13 }}>{comment.authorName}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{formatDate(comment.createdAt)}</Text>
                    </div>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</Text>
                  </Card>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            <Form form={commentForm} onFinish={handleAddComment} scrollToFirstError>
              <Form.Item name="content" rules={[{ required: true, message: '' }]}>
                <TextArea rows={2} placeholder={t('tasks:commentPlaceholder')} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={submitting} size="small">
                  {t('tasks:addComment')}
                </Button>
              </Form.Item>
            </Form>

            {/* Activity Timeline */}
            {selectedTask.activities && selectedTask.activities.length > 0 && (
              <>
                <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
                  <ClockCircleOutlined /> {t('tasks:activity')}
                </Title>
                <Timeline
                  items={selectedTask.activities.map(act => ({
                    children: (
                      <div>
                        <Text strong>{act.actorName}</Text>{' '}
                        <Text>{t(`tasks:actions.${act.action}`)}</Text>
                        {act.action === 'STATUS_CHANGED' && act.details && (
                          <Text type="secondary">
                            {' '}({t(`tasks:statuses.${act.details.from}`)} → {t(`tasks:statuses.${act.details.to}`)})
                          </Text>
                        )}
                        {act.action === 'ASSIGNED' && act.details?.to && (
                          <Text type="secondary">
                            {' '}→ {users.find(u => u.id === act.details.to)?.name || act.details.to}
                          </Text>
                        )}
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatDate(act.createdAt)}
                        </Text>
                      </div>
                    )
                  }))}
                />
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default InternalTasks;
